import { registerPlugin } from "@capacitor/core";

export interface MemoryStatsResult {
  /** Resident set size in bytes (iOS: task_vm_info.resident_size, Android: not provided). */
  rss?: number;
  /** iOS phys_footprint in bytes — what jetsam compares against the per-app limit. */
  physFootprint?: number;
  /** iOS task_vm_info.compressed in bytes. */
  compressed?: number;
  /**
   * iOS ledger_phys_footprint_peak in bytes — the kernel's own session high-water mark.
   * A polled sampler cannot observe a peak that occurs while the main thread is blocked;
   * this value is recorded by the kernel regardless of when we ask.
   */
  footprintPeak?: number;
  /**
   * iOS limit_bytes_remaining in bytes — headroom against this process's memory limit.
   * Measured as 0 on macOS, where no per-process limit applies; whether iOS populates it
   * is unverified on device, which is why `availableMemory` is recorded alongside it.
   */
  limitRemaining?: number;
  /**
   * iOS os_proc_available_memory() in bytes — the documented API for remaining headroom.
   * Absent when the API returned 0, which it does both when headroom is exhausted and when
   * the process is subject to no memory limit at all. Those two readings are opposite
   * conclusions, so a 0 is never reported: absent means "the API said nothing".
   */
  availableMemory?: number;
  /** iOS task_vm_info.internal in bytes — memory backed by our own allocations. */
  internalBytes?: number;
  /** iOS task_vm_info.external in bytes — mapped files and shared memory. */
  externalBytes?: number;
  /** iOS task_vm_info.compressed_peak in bytes. */
  compressedPeak?: number;
  /** Android Debug.MemoryInfo total PSS in bytes (≈ iOS RSS analogue). */
  totalPss?: number;
  /** Android native heap PSS in bytes. */
  nativePss?: number;
  /** Android dalvik (managed) heap PSS in bytes. */
  dalvikPss?: number;
  /** Android Runtime java heap used (total - free) in bytes. */
  javaHeapUsed?: number;
  /** Android-wide ActivityManager available bytes. */
  systemAvail?: number;
  /** Android lowMemory flag from ActivityManager.MemoryInfo. */
  systemLowMemory?: boolean;
}

export interface MemoryStatsPlugin {
  getCurrent(): Promise<MemoryStatsResult>;
}

export const MemoryStats = registerPlugin<MemoryStatsPlugin>("MemoryStats");
